"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useUniversalToken = useUniversalToken;
var _react = require("react");
var _constants = require("../constants");
var _internals = require("../internals");
var _getTokenClients = require("../internals/getTokenClients");
var _logger = require("../logger");
var _decodeToken = require("../utils/decodeToken");
var _isTokenValid = require("../utils/isTokenValid");
const initialAnonymousTokenState = {
  customerId: _constants.NOT_LOGGED_IN_USER_ID,
  cleanToken: undefined
};
const logger = _logger.mainLogger.createChild('useUniversalToken');
function useUniversalToken({
  customerId,
  initialToken
}) {
  const cleanInitialToken = initialToken ? (0, _internals.cleanToken)(initialToken) : undefined;
  const validInitialToken = cleanInitialToken && (0, _isTokenValid.isTokenValid)(cleanInitialToken) ? cleanInitialToken : undefined;
  const [tokenData, setTokenData] = (0, _react.useState)(
  // Take customerId from props only when initial token is valid, otherwise just set the default (not logged in)
  validInitialToken ? {
    customerId,
    cleanToken: validInitialToken
  } : initialAnonymousTokenState);
  const [isUserVerified, setIsUserVerified] = (0, _react.useState)(false);
  const updateTokenData = newTokenData => {
    const newToken = newTokenData?.Token ? (0, _internals.cleanToken)(newTokenData.Token) : undefined;
    const decodedToken = newToken ? (0, _decodeToken.decodeToken)(newToken) : null;
    setTokenData({
      customerId: decodedToken?.customer_id != null ? decodedToken.customer_id : _constants.NOT_LOGGED_IN_USER_ID,
      cleanToken: newToken
    });
  };

  // try to fetch new token if available on the first render (this will result in loading of the token and customerId of signed-in user)
  (0, _react.useEffect)(() => {
    const {
      publicTokenClient,
      userTokenClient
    } = (0, _getTokenClients.getTokenClients)();
    const tokenChangedListener = event => updateTokenData(event.detail.tokenData);
    const fetchToken = async () => {
      try {
        updateTokenData(await userTokenClient.getToken());
        userTokenClient.bindTokenUpdated(tokenChangedListener);
      } catch (error) {
        updateTokenData(await publicTokenClient.getToken());
        publicTokenClient.bindTokenUpdated(tokenChangedListener);
      }
    };

    // TODO(OL): There should be an abort controller
    fetchToken().catch(error => {
      logger.error(new Error(`Some error occurred while getting token`, {
        cause: error
      }));
      setTokenData(initialAnonymousTokenState);
    }).finally(() => setIsUserVerified(true));
    return () => {
      publicTokenClient.unbindTokenUpdated(tokenChangedListener);
      userTokenClient.unbindTokenUpdated(tokenChangedListener);
    };
  }, []);
  return (0, _react.useMemo)(() => ({
    token: tokenData.cleanToken ?? '',
    // TODO (JN): This should be nullable to reflect the real state of the app. Consumers then may decide what to do when token is not available. But it is going to be a breaking change.
    customerId: tokenData.customerId ?? _constants.NOT_LOGGED_IN_USER_ID,
    formattedToken: tokenData.cleanToken ? `Bearer ${tokenData.cleanToken}` : '',
    isAnonymousUser: tokenData.customerId === _constants.NOT_LOGGED_IN_USER_ID,
    isUserVerified
  }), [tokenData.customerId, tokenData.cleanToken, isUserVerified]);
}
